import React, { useState, useEffect } from 'react';
import { Search, Calendar, UserPlus, Mail, Trash2, AlertTriangle } from 'lucide-react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

import { supabase } from '../../../lib/supabase';
import { authService } from '../../../services/authService';
import AddUserModal from './AddUserModal';

const UserManagementPanel = () => {
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetDate, setResetDate] = useState('');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isResendingEmail, setIsResendingEmail] = useState(false);
  const [resendingUserId, setResendingUserId] = useState(null);
  const [alertMessage, setAlertMessage] = useState({ type: '', message: '' });
  const [userConfirmationStatus, setUserConfirmationStatus] = useState({});

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      const { data, error } = await supabase
        ?.from('user_profiles')
        ?.select('*')
        ?.order('created_at', { ascending: false });
      
      if (error) throw error;
      setUsers(data);

      // Fetch confirmation status for each user
      const statusPromises = data?.map(async (user) => {
        const status = await authService?.getUserConfirmationStatus(user?.id);
        return { userId: user?.id, ...status };
      });

      const statuses = await Promise.all(statusPromises);
      const statusMap = {};
      statuses?.forEach(status => {
        statusMap[status?.userId] = status;
      });
      setUserConfirmationStatus(statusMap);
    } catch (error) {
      console.error('Error fetching clients:', error);
      showAlert('error', 'Failed to fetch clients');
    }
  };

  const handleAddUserSuccess = () => {
    fetchClients();
  };

  const showAlert = (type, message) => {
    setAlertMessage({ type, message });
    setTimeout(() => {
      setAlertMessage({ type: '', message: '' });
    }, 5000);
  };

  const handleResendConfirmationEmail = async (user) => {
    setIsResendingEmail(true);
    setResendingUserId(user?.id);

    const result = await authService?.resendConfirmationEmail(user?.email);

    if (result?.success) {
      showAlert('success', `Confirmation email sent to ${user?.email}`);
    } else {
      showAlert('error', result?.message || 'Failed to send confirmation email');
    }

    setIsResendingEmail(false);
    setResendingUserId(null);
  };

  const openDeleteModal = (user) => {
    setUserToDelete(user);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setUserToDelete(null);
    setShowDeleteModal(false);
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;

    setIsDeleting(true);

    const result = await authService?.deleteUser(userToDelete?.id);

    if (result?.success) {
      showAlert('success', `User ${userToDelete?.full_name} deleted successfully`);
      closeDeleteModal();
      fetchClients(); // Refresh the list
    } else {
      showAlert('error', result?.message || 'Failed to delete user');
    }

    setIsDeleting(false);
  };

  const filteredUsers = users?.filter(user =>
    user?.email?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
    user?.full_name?.toLowerCase()?.includes(searchTerm?.toLowerCase())
  );

  const onResetStartDate = async (userId, newStartDate) => {
    try {
      const { error } = await supabase
        ?.from('user_profiles')
        ?.update({ program_start_date: newStartDate })
        ?.eq('id', userId);
      
      if (error) throw error;
      
      showAlert('success', 'Program start date updated successfully');
      fetchClients();
    } catch (error) {
      console.error('Error resetting start date:', error);
      showAlert('error', 'Failed to update start date');
    }
  };

  const handleResetStartDate = () => {
    if (selectedUser && resetDate) {
      onResetStartDate(selectedUser?.id, resetDate);
      setShowResetModal(false);
      setSelectedUser(null);
      setResetDate('');
    }
  };

  const openResetModal = (user) => {
    setSelectedUser(user);
    setResetDate(new Date()?.toISOString()?.split('T')?.[0]);
    setShowResetModal(true);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-success/10 text-success';
      case 'inactive': return 'bg-error/10 text-error';
      case 'completed': return 'bg-primary/10 text-primary';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const getDaysInProgram = (startDate) => {
    if (!startDate) return 0;
    const start = new Date(startDate);
    const today = new Date();
    const diffTime = Math.abs(today - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.min(diffDays, 42);
  };

  return (
    <div className="space-y-6">
      {/* Alert Message */}
      {alertMessage?.message && (
        <div className={`p-4 rounded-lg ${
          alertMessage?.type === 'success' ?'bg-green-50 border border-green-200 text-green-800' :'bg-red-50 border border-red-200 text-red-800'
        }`}>
          <p className="text-sm font-medium">{alertMessage?.message}</p>
        </div>
      )}

      {/* Header with Add Button */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-[#1A2B4A]">Client Management</h2>
          <p className="text-gray-600 text-sm mt-1">
            Manage client accounts and program configurations
          </p>
        </div>
        <Button
          onClick={() => setIsAddModalOpen(true)}
          className="flex items-center gap-2"
        >
          <UserPlus className="w-4 h-4" />
          Add New Client
        </Button>
      </div>

      {/* Search */}
      <div className="mt-4">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search clients..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e?.target?.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      {/* Desktop Table */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="text-left p-4 font-medium text-gray-900">Client</th>
              <th className="text-left p-4 font-medium text-gray-900">Status</th>
              <th className="text-left p-4 font-medium text-gray-900">Email Verified</th>
              <th className="text-left p-4 font-medium text-gray-900">Program Day</th>
              <th className="text-left p-4 font-medium text-gray-900">Start Date</th>
              <th className="text-left p-4 font-medium text-gray-900">Last Activity</th>
              <th className="text-right p-4 font-medium text-gray-900">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers?.map((user) => {
              const confirmStatus = userConfirmationStatus?.[user?.id];
              return (
                <tr key={user?.id} className="border-t border-gray-200 hover:bg-gray-50 transition-colors">
                  <td className="p-4">
                    <div className="flex items-center space-x-3">
                      <div className="flex items-center justify-center w-10 h-10 bg-blue-100 rounded-full">
                        <span className="text-sm font-medium text-blue-600">
                          {user?.full_name?.charAt(0)?.toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <h3 className="font-medium text-gray-900">
                          {user?.full_name}
                        </h3>
                        <p className="text-sm text-gray-500">
                          {user?.email}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="p-4">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(user?.status)}`}>
                      {user?.status?.charAt(0)?.toUpperCase() + user?.status?.slice(1)}
                    </span>
                  </td>
                  <td className="p-4">
                    {confirmStatus?.confirmed ? (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        ✓ Verified
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                        ⚠ Unverified
                      </span>
                    )}
                  </td>
                  <td className="p-4">
                    <div className="flex items-center space-x-2">
                      <span className="font-medium text-gray-900">
                        {getDaysInProgram(user?.program_start_date)}
                      </span>
                      <span className="text-sm text-gray-500">of 42</span>
                    </div>
                  </td>
                  <td className="p-4">
                    <span className="text-sm text-gray-900">
                      {user?.program_start_date ? new Date(user.program_start_date)?.toLocaleDateString() : 'Not set'}
                    </span>
                  </td>
                  <td className="p-4">
                    <span className="text-sm text-gray-500">
                      {user?.updated_at ? new Date(user.updated_at)?.toLocaleDateString() : 'Never'}
                    </span>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center justify-end space-x-2">
                      {!confirmStatus?.confirmed && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleResendConfirmationEmail(user)}
                          disabled={isResendingEmail && resendingUserId === user?.id}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          <Mail className="w-4 h-4 mr-1" />
                          {isResendingEmail && resendingUserId === user?.id ? 'Sending...' : 'Resend Email'}
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openResetModal(user)}
                      >
                        <Calendar className="w-4 h-4 mr-1" />
                        Reset Date
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openDeleteModal(user)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <Trash2 className="w-4 h-4 mr-1" />
                        Delete
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile Cards */}
      <div className="lg:hidden p-4 space-y-4">
        {filteredUsers?.map((user) => {
          const confirmStatus = userConfirmationStatus?.[user?.id];
          return (
            <div key={user?.id} className="bg-white rounded-lg p-4 space-y-3 shadow-sm border border-gray-200">
              <div className="flex items-start justify-between">
                <div className="flex items-center space-x-3">
                  <div className="flex items-center justify-center w-12 h-12 bg-blue-100 rounded-full">
                    <span className="font-medium text-blue-600">
                      {user?.full_name?.charAt(0)?.toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900">
                      {user?.full_name}
                    </h3>
                    <p className="text-sm text-gray-500">
                      {user?.email}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(user?.status)}`}>
                        {user?.status?.charAt(0)?.toUpperCase() + user?.status?.slice(1)}
                      </span>
                      {confirmStatus?.confirmed ? (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          ✓ Verified
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                          ⚠ Unverified
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Program Day:</span>
                  <div className="font-medium text-gray-900">
                    {getDaysInProgram(user?.program_start_date)} of 42
                  </div>
                </div>
                <div>
                  <span className="text-gray-500">Start Date:</span>
                  <div className="text-gray-900">
                    {user?.program_start_date ? new Date(user.program_start_date)?.toLocaleDateString() : 'Not set'}
                  </div>
                </div>
              </div>
              
              <div className="text-sm">
                <span className="text-gray-500">Last Activity: </span>
                <span className="text-gray-900">
                  {user?.updated_at ? new Date(user.updated_at)?.toLocaleDateString() : 'Never'}
                </span>
              </div>
              
              <div className="space-y-2 pt-2">
                {!confirmStatus?.confirmed && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleResendConfirmationEmail(user)}
                    disabled={isResendingEmail && resendingUserId === user?.id}
                    fullWidth
                    className="text-blue-600 border-blue-300 hover:bg-blue-50"
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    {isResendingEmail && resendingUserId === user?.id ? 'Sending...' : 'Resend Confirmation Email'}
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => openResetModal(user)}
                  fullWidth
                >
                  <Calendar className="w-4 h-4 mr-2" />
                  Reset Start Date
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => openDeleteModal(user)}
                  fullWidth
                  className="text-red-600 border-red-300 hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete User
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Empty State */}
      {filteredUsers?.length === 0 && (
        <div className="p-12 text-center">
          <Calendar className="text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {searchTerm ? 'No clients found' : 'No clients yet'}
          </h3>
          <p className="text-gray-500">
            {searchTerm 
              ? 'Try adjusting your search terms' : 'Clients will appear here once they join the program'
            }
          </p>
        </div>
      )}

      {/* Delete User Confirmation Modal */}
      {showDeleteModal && userToDelete && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white border border-gray-200 rounded-lg shadow-lg max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="flex items-center justify-center w-10 h-10 bg-red-100 rounded-lg">
                  <AlertTriangle className="text-red-600 w-6 h-6" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Delete User
                  </h3>
                  <p className="text-sm text-gray-500">
                    This action cannot be undone
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-sm text-red-800 font-medium mb-2">
                    Warning: This will permanently delete:
                  </p>
                  <ul className="text-sm text-red-700 space-y-1 ml-4 list-disc">
                    <li>User account: {userToDelete?.full_name}</li>
                    <li>Email: {userToDelete?.email}</li>
                    <li>All associated data (goals, readings, activities)</li>
                    <li>Program progress and history</li>
                  </ul>
                </div>

                <p className="text-sm text-gray-600">
                  Are you sure you want to delete this user? This action is permanent and cannot be reversed.
                </p>

                <div className="flex items-center space-x-3 pt-4">
                  <Button
                    variant="outline"
                    onClick={closeDeleteModal}
                    disabled={isDeleting}
                    fullWidth
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="danger"
                    onClick={handleDeleteUser}
                    disabled={isDeleting}
                    fullWidth
                    className="bg-red-600 hover:bg-red-700 text-white"
                  >
                    {isDeleting ? 'Deleting...' : 'Delete User'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reset Start Date Modal */}
      {showResetModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white border border-gray-200 rounded-lg shadow-lg max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="flex items-center justify-center w-10 h-10 bg-yellow-100 rounded-lg">
                  <Calendar className="text-yellow-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Reset Start Date
                  </h3>
                  <p className="text-sm text-gray-500">
                    {selectedUser?.full_name}
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                <p className="text-sm text-gray-600">
                  This will reset the client's program progress and start their 42-day curriculum from the selected date.
                </p>

                <Input
                  label="New Start Date"
                  type="date"
                  value={resetDate}
                  onChange={(e) => setResetDate(e?.target?.value)}
                  required
                />

                <div className="flex items-center space-x-3 pt-4">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowResetModal(false);
                      setSelectedUser(null);
                      setResetDate('');
                    }}
                    fullWidth
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="warning"
                    onClick={handleResetStartDate}
                    disabled={!resetDate}
                    fullWidth
                  >
                    Reset Date
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add User Modal */}
      <AddUserModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSuccess={handleAddUserSuccess}
      />
    </div>
  );
};

export default UserManagementPanel;